import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { theme } from "@src/theme";
import SingleSelectWithInfiniteScroll from "./SingleSelectWithInfiniteScroll";
import { ITag } from "@src/types/tags";
import {
  useFetchGenresTagsInfiniteQuery,
  useFetchFeelingTagsInfiniteQuery,
} from "@src/queries/tagQueries";
import { useDebounce } from "@src/hooks/useDebounce";
import { AxiosResponse } from "axios";
import { ApiResponse } from "@src/types/httpApi";
import { UIButton } from "@src/components/UIButton";
import UIModal from "@src/components/UIModal/UIModal";
import { ITrack } from "@src/types/track";

type UpdateTrackTagsModalProps = {
  onConfirm: (
    genreIds?: string[],
    emotionIds?: string[],
    subGenre?: ITag | null
  ) => void;
  onClose: () => void;
  isOpen: boolean;
  loading?: boolean;
  track: ITrack | null;
  tagType: "genre" | "emotion";
  subGenreOnly?: boolean;
};

const UpdateTrackTagsModal: React.FC<UpdateTrackTagsModalProps> = ({
  onConfirm,
  onClose,
  isOpen,
  loading,
  track,
  tagType,
  subGenreOnly = false,
}) => {
  const [selectedTag, setSelectedTag] = useState<ITag | null>(null);
  const [selectedSubGenre, setSelectedSubGenre] = useState<ITag | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const debouncedSearchText = useDebounce<string>(searchText, 600);
  const trackTagIdRef = useRef<string | null>(null);

  const useTagsQuery =
    tagType === "genre"
      ? useFetchGenresTagsInfiniteQuery
      : useFetchFeelingTagsInfiniteQuery;

  const {
    data: tagsResponse,
    isLoading: isTagsLoading,
    isError: isTagsError,
    error: tagsError,
    isFetching: isTagsFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTagsQuery({
    searchText: debouncedSearchText,
    enabled: isOpen && !subGenreOnly, // Only fetch genres when editing genre, not sub-genre only
  });

  const memorizedTags = useMemo(() => {
    if (!tagsResponse?.pages || !Array.isArray(tagsResponse.pages)) {
      return [];
    }
    const allTags =
      (tagsResponse.pages as AxiosResponse<ApiResponse<ITag[]>>[]).flatMap(
        (response) => {
          if (!response?.data?.data || !Array.isArray(response.data.data)) {
            return [];
          }
          return response.data.data;
        }
      ) || [];
    return allTags;
  }, [tagsResponse]);

  const optionsEndRef = useRef<HTMLDivElement | null>(null);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  const getTrackSubGenreId = useCallback(() => {
    const rawSubGenre = track?.subGenre;
    if (!rawSubGenre) return null;

    if (typeof rawSubGenre === "string") {
      const trimmed = rawSubGenre.trim();
      return trimmed.length > 0 ? trimmed : null;
    }

    if (Array.isArray(rawSubGenre)) {
      const firstSubGenre = rawSubGenre[0];
      return firstSubGenre && typeof firstSubGenre === "object"
        ? firstSubGenre.documentId || null
        : null;
    }

    return rawSubGenre.documentId || null;
  }, [track?.subGenre]);

  const getTrackSubGenreOption = useCallback(() => {
    const rawSubGenre = track?.subGenre;
    if (!rawSubGenre) return null;

    if (typeof rawSubGenre === "string") {
      const trimmed = rawSubGenre.trim();
      return trimmed.length > 0
        ? ({
            documentId: trimmed,
            title: trimmed,
            description: "",
            color: "",
          } as ITag)
        : null;
    }

    if (Array.isArray(rawSubGenre)) {
      const firstSubGenre = rawSubGenre[0];
      return firstSubGenre && typeof firstSubGenre === "object"
        ? (firstSubGenre as ITag)
        : null;
    }

    return rawSubGenre as ITag;
  }, [track?.subGenre]);

  const genreForSubGenres = useMemo(() => {
    if (subGenreOnly && track?.genre) {
      const g = track.genre;
      return !Array.isArray(g) ? g : (g[0] ?? null);
    }
    return selectedTag;
  }, [subGenreOnly, track?.genre, selectedTag]);

  const subGenreOptions = useMemo(() => {
    const rawSubGenres = genreForSubGenres?.subGenres;
    if (!Array.isArray(rawSubGenres)) return [];

    const normalized = rawSubGenres
      .map((item): ITag | null => {
        if (typeof item === "string") {
          const trimmed = item.trim();
          return trimmed.length > 0
            ? {
                documentId: trimmed,
                id: 0,
                title: trimmed,
                description: "",
                color: "",
              }
            : null;
        }
        if (item && typeof item === "object") {
          const docId =
            (item as { documentId?: string }).documentId ||
            (item as { id?: string }).id;
          const title =
            (item as { title?: string }).title ||
            (item as { name?: string }).name ||
            (item as { label?: string }).label ||
            "";
          if (docId || title) {
            return {
              documentId: String(docId || title),
              id: 0,
              title: String(title || docId),
              description: "",
              color: "",
            };
          }
        }
        return null;
      })
      .filter((t): t is ITag => t !== null);

    const trackSubGenreOption = getTrackSubGenreOption();
    if (!trackSubGenreOption) return normalized;

    const alreadyIncluded = normalized.some(
      (opt) => opt.documentId === trackSubGenreOption.documentId
    );
    return alreadyIncluded ? normalized : [trackSubGenreOption, ...normalized];
  }, [genreForSubGenres?.subGenres, getTrackSubGenreOption]);

  useEffect(() => {
    if (!isOpen) return;

    let observer: IntersectionObserver | null = null;

    const timeoutId = setTimeout(() => {
      if (!optionsEndRef.current) return;

      observer = new IntersectionObserver(handleObserver, {
        root: null,
        rootMargin: "200px",
        threshold: 0.1,
      });

      const currentRef = optionsEndRef.current;
      observer.observe(currentRef);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (observer && optionsEndRef.current) {
        observer.unobserve(optionsEndRef.current);
      }
    };
  }, [isOpen, handleObserver, memorizedTags.length]);

  // Initialize selection only when modal opens or track/tagType changes
  useEffect(() => {
    if (!isOpen) {
      trackTagIdRef.current = null;
      setSelectedTag(null);
      return;
    }
    if (subGenreOnly) return;

    // Emotion and genre are objects, not arrays
    const trackTag = tagType === "genre" ? track?.genre : track?.emotion;
    const trackTagObject =
      trackTag && !Array.isArray(trackTag)
        ? trackTag
        : Array.isArray(trackTag) && trackTag.length > 0
          ? trackTag[0]
          : null;

    const currentTrackTagId = trackTagObject?.documentId || null;

    // Only initialize if track tag changed or it's the first time opening
    if (trackTagIdRef.current !== currentTrackTagId) {
      trackTagIdRef.current = currentTrackTagId;

      if (currentTrackTagId && memorizedTags.length > 0) {
        const normalizedTag = memorizedTags.find(
          (mTag) => mTag.documentId === currentTrackTagId
        );
        // Use a small delay to ensure the selector updates properly
        const timeoutId = setTimeout(() => {
          setSelectedTag(normalizedTag || null);
        }, 100);
        return () => clearTimeout(timeoutId);
      } else {
        const timeoutId = setTimeout(() => {
          setSelectedTag(null);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [track?.genre, track?.emotion, tagType, isOpen, subGenreOnly]);

  useEffect(() => {
    if (!isOpen) return;

    if (tagType !== "genre") {
      setSelectedSubGenre(null);
      return;
    }

    if (subGenreOnly) {
      const trackSubGenre = getTrackSubGenreId();
      if (!trackSubGenre) {
        setSelectedSubGenre(null);
        return;
      }
      const matchingOption = subGenreOptions.find(
        (option) => option.documentId === trackSubGenre
      );
      setSelectedSubGenre(matchingOption || null);
      return;
    }

    const trackTag = track?.genre;
    const trackTagObject =
      trackTag && !Array.isArray(trackTag)
        ? trackTag
        : Array.isArray(trackTag) && trackTag.length > 0
          ? trackTag[0]
          : null;
    const trackTagId = trackTagObject?.documentId || null;

    if (!selectedTag?.documentId || selectedTag.documentId !== trackTagId) {
      setSelectedSubGenre(null);
      return;
    }

    const trackSubGenre = getTrackSubGenreId();
    if (!trackSubGenre) {
      setSelectedSubGenre(null);
      return;
    }

    const matchingOption = subGenreOptions.find(
      (option) => option.documentId === trackSubGenre
    );
    setSelectedSubGenre(matchingOption || null);
  }, [
    getTrackSubGenreId,
    isOpen,
    subGenreOptions,
    tagType,
    subGenreOnly,
    track?.genre,
    selectedTag?.documentId,
  ]);

  useEffect(() => {
    if (!isOpen || tagType !== "genre" || subGenreOnly) return;

    const trackTag = track?.genre;
    const trackTagObject =
      trackTag && !Array.isArray(trackTag)
        ? trackTag
        : Array.isArray(trackTag) && trackTag.length > 0
          ? trackTag[0]
          : null;
    const trackTagId = trackTagObject?.documentId || null;

    if (selectedTag?.documentId && selectedTag.documentId === trackTagId) {
      return;
    }

    setSelectedSubGenre(null);
  }, [selectedTag?.documentId, isOpen, tagType, subGenreOnly, track?.genre]);

  // If tags become available after initial load and we have a track tag but no selection, find it
  useEffect(() => {
    if (!isOpen || subGenreOnly) return;

    const trackTag = tagType === "genre" ? track?.genre : track?.emotion;
    const trackTagObject =
      trackTag && !Array.isArray(trackTag)
        ? trackTag
        : Array.isArray(trackTag) && trackTag.length > 0
          ? trackTag[0]
          : null;

    const currentTrackTagId = trackTagObject?.documentId || null;

    // Only try to find tag if we have a track tag ID, no current selection, and tags are available
    if (
      currentTrackTagId &&
      !selectedTag &&
      memorizedTags.length > 0 &&
      trackTagIdRef.current === currentTrackTagId
    ) {
      const normalizedTag = memorizedTags.find(
        (mTag) => mTag.documentId === currentTrackTagId
      );
      if (normalizedTag) {
        const timeoutId = setTimeout(() => {
          setSelectedTag(normalizedTag);
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [
    memorizedTags,
    isOpen,
    subGenreOnly,
    track?.genre,
    track?.emotion,
    tagType,
    selectedTag,
  ]);

  const checkIfTagsChanged = () => {
    if (subGenreOnly) {
      const trackSubGenre = getTrackSubGenreId();
      const selectedSubGenreValue = selectedSubGenre?.documentId || null;
      return trackSubGenre !== selectedSubGenreValue;
    }
    // Emotion and genre are objects, not arrays
    const trackTag = tagType === "genre" ? track?.genre : track?.emotion;
    const trackTagObject =
      trackTag && !Array.isArray(trackTag)
        ? trackTag
        : Array.isArray(trackTag) && trackTag.length > 0
          ? trackTag[0]
          : null;

    if (trackTagObject) {
      const currentTagId = trackTagObject.documentId;
      const selectedTagId = selectedTag?.documentId;
      return currentTagId !== selectedTagId;
    }

    return selectedTag !== null;
  };

  const onCancelSubmit = () => {
    onClose();
    if (subGenreOnly) {
      const trackSubGenre = getTrackSubGenreId();
      const matchingOption = subGenreOptions.find(
        (option) => option.documentId === trackSubGenre
      );
      setSelectedSubGenre(matchingOption || null);
      return;
    }
    // Emotion and genre are objects, not arrays
    const trackTag = tagType === "genre" ? track?.genre : track?.emotion;
    const trackTagObject =
      trackTag && !Array.isArray(trackTag)
        ? trackTag
        : Array.isArray(trackTag) && trackTag.length > 0
          ? trackTag[0]
          : null;

    if (trackTagObject && memorizedTags.length > 0) {
      const normalizedTag = memorizedTags.find(
        (mTag) => mTag.documentId === trackTagObject.documentId
      );
      setSelectedTag(normalizedTag || null);
    } else {
      setSelectedTag(null);
    }

    if (tagType === "genre") {
      const trackSubGenre = getTrackSubGenreId();
      const matchingOption = subGenreOptions.find(
        (option) => option.documentId === trackSubGenre
      );
      setSelectedSubGenre(matchingOption || null);
    }
  };

  const handleConfirm = () => {
    if (subGenreOnly) {
      const genre = track?.genre;
      const genreId =
        genre && !Array.isArray(genre)
          ? genre.documentId
          : Array.isArray(genre) && genre.length > 0
            ? (genre[0]?.documentId ?? null)
            : null;
      onConfirm(genreId ? [genreId] : [], undefined, selectedSubGenre || null);
      return;
    }
    const selectedTagId = selectedTag?.documentId;
    if (tagType === "genre") {
      onConfirm(selectedTagId ? [selectedTagId] : [], undefined, null);
    } else {
      onConfirm(undefined, selectedTagId ? [selectedTagId] : []);
    }
  };

  return (
    <>
      <UIModal
        modalStyles={{
          width: 600,
        }}
        onClose={onClose}
        isOpen={isOpen}
        actions={[
          <UIButton
            key="cancel"
            onClick={onCancelSubmit}
            variant="secondaryOutline"
          >
            Cancel
          </UIButton>,
          <UIButton
            key="submit"
            onClick={handleConfirm}
            variant="primary"
            disabled={
              isTagsLoading ||
              isTagsFetching ||
              loading ||
              !checkIfTagsChanged()
            }
            style={{ minWidth: 100 }}
          >
            {loading ? "Updating..." : "Submit"}
          </UIButton>,
        ]}
        headerStyles={{
          textAlign: "center",
        }}
        title={
          subGenreOnly
            ? "Sub-genre Tags"
            : `${tagType === "genre" ? "Genre" : "Emotion"} Tags`
        }
      >
        <div style={{ padding: theme.margins.medium }}>
          {isTagsError && !subGenreOnly ? (
            <div>Error loading tags: {tagsError?.message}</div>
          ) : subGenreOnly ? (
            <SingleSelectWithInfiniteScroll<ITag>
              options={subGenreOptions}
              selected={selectedSubGenre}
              onSelect={setSelectedSubGenre}
              labelKey={"title"}
              valueKey={"documentId"}
              placeholder="Select sub-genre"
              disabled={subGenreOptions.length === 0}
              disabledTitle="This genre has no sub-genres"
            />
          ) : (
            <SingleSelectWithInfiniteScroll<ITag>
              options={memorizedTags || []}
              selected={selectedTag}
              onSelect={setSelectedTag}
              labelKey={"title"}
              valueKey={"documentId"}
              placeholder={`Select ${tagType === "genre" ? "genre" : "emotion"}`}
              loading={isTagsLoading || isTagsFetching}
              hasNextPage={hasNextPage || false}
              isFetchingNextPage={isFetchingNextPage || false}
              onLoadMore={fetchNextPage}
              loaderRef={optionsEndRef}
              searchText={searchText}
              onSearchTextChange={setSearchText}
            />
          )}
        </div>
      </UIModal>
    </>
  );
};

export default UpdateTrackTagsModal;
